import { AnimatedHeight } from 'fukku/AnimatedHeight/AnimatedHeight'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import {
	Children,
	type ForwardRefExoticComponent,
	type ReactNode,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import { cx } from 'utils/cx'

import styles from './Steps.module.scss'

const MOTION_SHORT1 = 100

/* eslint react-hooks/exhaustive-deps: warn */

export interface StepsContext<T = unknown> {
	lastIndex: number | null
	/** To share date between steps */
	detail: T
}

interface StepsProps<T> {
	onNext?: (index: number) => void
	onPrev?: (index: number) => void
	onChange?: (index: number) => void
	children: (
		next: () => void,
		prev: () => void,
		ctx: StepsContext<T>
	) => ReactNode[]
}

const NO_ANIMATION = ''

export interface StepsApi<T> {
	next: () => void
	prev: () => void
	ctx: StepsContext<T>
}

export function StepsComponent<T>(
	{ children, onNext, onPrev, onChange }: Readonly<StepsProps<T>>,
	ref: React.Ref<StepsApi<T>>
) {
	const {
		country: { isRtl },
	} = useMasterData()
	const [index, setIndex] = useState(0)
	const stepsCtx = useRef<StepsContext<T>>({ lastIndex: null, detail: {} as T })
	const innerRef = useRef<HTMLDivElement>(null)
	const [animation, setAnimation] = useState<string>('')

	const next = useCallback(() => {
		setAnimation(styles.next)
		onNext?.(index)
		innerRef.current?.addEventListener(
			'animationend',
			() => {
				setIndex((prevIndex) => {
					stepsCtx.current.lastIndex = prevIndex
					return prevIndex + 1
				})
				setAnimation(NO_ANIMATION)
			},
			{ once: true }
		)
	}, [index, onNext])

	const prev = useCallback(() => {
		setAnimation(styles.prev)
		onPrev?.(index)
		innerRef.current?.addEventListener(
			'animationend',
			() => {
				setIndex((prevIndex) => {
					stepsCtx.current.lastIndex = prevIndex
					return prevIndex - 1
				})
				setAnimation(NO_ANIMATION)
			},
			{ once: true }
		)
	}, [index, onPrev])

	useImperativeHandle(ref, () => ({ next, prev, ctx: stepsCtx.current }), [
		next,
		prev,
		stepsCtx,
	])

	const steps = children(next, prev, stepsCtx.current)
	const max = Children.count(steps) - 1

	const currentStep = useMemo(
		() => steps[Math.max(0, Math.min(index, max))],
		[index, max, steps]
	)

	const prevStep = useMemo(() => {
		const prevIndex = Math.max(-1, Math.min(index - 1, max))
		if (prevIndex < 0 || animation !== styles.prev) {
			return null
		}
		return steps[prevIndex]
	}, [index, max, animation, steps])

	const nextStep = useMemo(() => {
		const nextIndex = Math.max(0, Math.min(index + 1, max + 1))
		if (nextIndex > max || animation !== styles.next) {
			return null
		}
		return steps[nextIndex]
	}, [index, max, animation, steps])

	useEffect(() => {
		onChange?.(index)
	}, [index, onChange])

	return (
		<AnimatedHeight
			data-steps-container
			delay={MOTION_SHORT1}
			className={styles.steps}
		>
			<div className={cx(styles.inner, animation)} ref={innerRef}>
				<div className={styles.step}>{isRtl ? nextStep : prevStep}</div>
				<div className={styles.step}>{currentStep}</div>
				<div className={styles.step}>{isRtl ? prevStep : nextStep}</div>
			</div>
		</AnimatedHeight>
	)
}

function typedForwardRef<T, P = {}>(
	render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
	return forwardRef(render) as ForwardRefExoticComponent<React.RefAttributes<T>>
}

export const Steps = typedForwardRef(StepsComponent)
